import React from 'react';
import { Helmet } from 'react-helmet-async';

const PrivacyPolicy: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Privacy, GDPR & Consent | Sharon Clare Hypnotherapy</title>
        <meta
          name="description"
          content="Privacy, GDPR and consent information for Sharon Clare Hypnotherapy — how your data is collected, stored, used and protected, and what you are agreeing to when you book a session."
        />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/privacy-policy/" />
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 py-16">
        <h1 className="text-[28px] font-heading font-bold text-gray-900 mb-4">
          Privacy, GDPR &amp; Consent
        </h1>
        <p className="text-gray-500 text-sm mb-2">Last updated: July 2026</p>
        <p className="text-gray-500 text-sm mb-8">ICO Registration Number: ZB676025</p>

        <p className="text-gray-700 leading-relaxed text-base mb-4">
          This page covers two things: how your information is handled when you use this website,
          and how your information is handled if you become a therapy client. Sharon Clare
          Hypnotherapy operates in accordance with the UK General Data Protection Regulation
          (UK GDPR) and the Data Protection Act 2018, as updated by the Data (Use and Access)
          Act 2025.
        </p>

        {/* 1. Data Controller */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          1. Who We Are
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          Sharon Clare Hypnotherapy is the data controller for the personal information you provide
          through this website, and for the client records held directly — session notes, contact
          details and related correspondence.
        </p>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          For clients seen at the Belfast and Lisburn clinics, the clinic is separately responsible,
          as data controller, for the booking and consent information held on its own systems (Jane
          and Phorest respectively). Each clinic issues its own privacy notice at the time of booking.
        </p>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          If you have any questions about this policy or how your data is handled, you can get in
          touch at:{' '}
          <a
            href="mailto:sharon@sharonclare-hypnotherapy.com"
            className="text-accent-600 hover:text-accent-700 underline"
          >
            sharon@sharonclare-hypnotherapy.com
          </a>
        </p>

        {/* 2. Consent to Solution-Focused Hypnotherapy */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          2. Consent to Solution-Focused Hypnotherapy
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          By booking a session, you agree that:
        </p>
        <ul className="list-disc list-inside text-gray-700 leading-relaxed text-base mb-4 space-y-2">
          <li>
            You are entering a course of Solution-Focused Hypnotherapy and accept your own
            responsibility in the process.
          </li>
          <li>
            You will engage in the positive activities suggested between sessions to maximise
            benefit.
          </li>
          <li>
            The number of sessions and the results are estimates only — no specific outcome can be
            guaranteed.
          </li>
          <li>Session notes will be held in accordance with UK GDPR and this statement.</li>
          <li>
            You give consent to be included in any therapist research programme (CORP), if
            applicable; all data will remain confidential.
          </li>
          <li>
            Your therapist may break confidentiality only to safeguard you or others, or to prevent
            a crime.
          </li>
          <li>
            This agreement is governed by UK law and subject to the exclusive jurisdiction of the UK
            courts.
          </li>
        </ul>

        {/* 3. How Your Data Is Stored and Used */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          3. How Your Data Is Stored and Used
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          Because hypnotherapy involves health-related information, this counts as
          &ldquo;special category data&rdquo; under GDPR. Sharon Clare Hypnotherapy relies on:
        </p>
        <ul className="list-disc list-inside text-gray-700 leading-relaxed text-base mb-4 space-y-2">
          <li>
            Your explicit consent to process health and therapy-related information
            (Article 9(2)(a)); and
          </li>
          <li>
            Contract / legitimate delivery of the service as the basis for processing the everyday
            contact information needed to arrange and run sessions (Article 6(1)(b)).
          </li>
        </ul>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          Only the minimum information needed to provide safe, effective therapy is collected. In
          particular, a full postal address is not requested unless there is a specific reason (for
          example, an insurer or regulator requires it), in line with the data minimisation
          principle. A date of birth is collected — this is used to confirm you are being offered
          age-appropriate consent arrangements and to apply the correct retention period below.
        </p>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          Identifiable data held may include:
        </p>
        <ul className="list-disc list-inside text-gray-700 leading-relaxed text-base mb-4 space-y-2">
          <li>Name, date of birth, and contact details (phone/email)</li>
          <li>GP details — only where you give specific, separate consent</li>
          <li>
            Session records, brief notes, text messages, or emails between you and Sharon Clare
            Hypnotherapy
          </li>
        </ul>

        {/* 4. Purpose of Data Collection */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          4. Purpose of Data Collection
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          To provide effective, continuous therapy, the following information is collected:
        </p>
        <ul className="list-disc list-inside text-gray-700 leading-relaxed text-base mb-4 space-y-2">
          <li>Your goals for hypnotherapy</li>
          <li>A small amount of medical information relevant to your treatment</li>
          <li>Brief session notes</li>
          <li>Date of birth, to confirm age-appropriate consent and apply the correct retention period</li>
          <li>Contact and GP details, used only with your explicit, separate consent</li>
          <li>CORP (research) data, if you choose to participate</li>
          <li>Relevant information about significant others, where helpful to the therapeutic process</li>
        </ul>

        {/* 5. Retention of Records */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          5. Retention of Records
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          Sharon Clare Hypnotherapy is a member of the Association for Solution Focused Hypnotherapy
          and the Complementary and Natural Healthcare Council. In line with their regulations and
          insurance requirements:
        </p>
        <ul className="list-disc list-inside text-gray-700 leading-relaxed text-base mb-4 space-y-2">
          <li>Adult client records are kept for 8 years after the final session.</li>
          <li>For clients under 16, records are kept until age 25.</li>
          <li>For clients aged 17–18, records are kept until age 26.</li>
        </ul>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          All records are deleted the following January once these periods expire. Due to insurance
          requirements, records cannot be destroyed before the minimum term.
        </p>

        {/* 6. Accessing Your Data */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          6. Accessing Your Data
        </h2>
        <ul className="list-disc list-inside text-gray-700 leading-relaxed text-base mb-4 space-y-2">
          <li>You may request a copy of your records at any time.</li>
          <li>Write to Sharon Clare Hypnotherapy specifying the data you wish to see.</li>
          <li>
            Your identity will be confirmed and a reasonable and proportionate search of your records
            carried out.
          </li>
          <li>
            Information will be provided within 30 days of your request (or of any identity check or
            clarification needed), free of charge. Where extra time is genuinely needed to clarify
            your request, that clarification period does not count towards the 30 days.
          </li>
          <li>The insurance company&rsquo;s legal team may need to verify any release of information.</li>
        </ul>

        {/* 7. Raising a Concern or Complaint */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          7. Raising a Concern or Complaint
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          If you have any concern about how your data is handled, please raise it directly with
          Sharon Clare Hypnotherapy first, by email or in writing:
        </p>
        <ul className="list-disc list-inside text-gray-700 leading-relaxed text-base mb-4 space-y-2">
          <li>Your complaint will be acknowledged within 30 days.</li>
          <li>It will be looked into and responded to without undue delay.</li>
        </ul>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          If you remain unhappy with the response, you also have the right to complain to the
          Information Commissioner&rsquo;s Office (ICO) at{' '}
          <a
            href="https://ico.org.uk"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-600 hover:text-accent-700 underline"
          >
            ico.org.uk
          </a>{' '}
          or on 0303 123 1113.
        </p>

        {/* 8. Security of Your Information */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          8. Security of Your Information
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          How and where your data is held depends on how we are in contact and, for clinic-based
          sessions, on the clinic you attend.
        </p>

        <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3 mt-6">
          Enquiries, booking and first contact
        </h3>
        <ul className="list-disc list-inside text-gray-700 leading-relaxed text-base mb-4 space-y-2">
          <li>
            For online clients and initial enquiries, first contact is usually via Calendly,
            WhatsApp, text, or email. Calendly is used for scheduling discovery calls and also hosts
            your consent form and this statement; it processes your name, email and phone number as
            part of managing your booking. See Calendly&rsquo;s own privacy policy for details of how
            they handle data.
          </li>
          <li>WhatsApp and text messages are held on a mobile phone secured by Face ID or passcode.</li>
          <li>
            Email is protected by device authentication (Face ID, username and password) and treated
            as a written record, stored securely.
          </li>
        </ul>

        <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3 mt-6">Session notes</h3>
        <ul className="list-disc list-inside text-gray-700 leading-relaxed text-base mb-4 space-y-2">
          <li>
            Enquiry and session notes are taken using the Notability app. Notes are uploaded to a
            secure OneDrive folder, and any identifying details are then removed from Notability, so
            Notability itself does not retain a searchable, identifiable record.
          </li>
          <li>
            OneDrive is the main secure store for client records. Access requires device
            authentication, and the client records area is additionally protected (for example,
            two-factor authentication and/or a locked, encrypted area such as OneDrive Personal
            Vault) so that files cannot be opened without further verification, even if the device
            itself is unlocked.
          </li>
        </ul>

        <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3 mt-6">
          Belfast clinic clients
        </h3>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          Booking details and your consent form are held on the clinic&rsquo;s own Jane system,
          operated and secured by the clinic, which issues its own privacy notice to you at the time
          of booking. Session notes from your hypnotherapy sessions are taken and stored in the same
          way described above (Notability → OneDrive).
        </p>

        <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3 mt-6">
          Lisburn clinic clients
        </h3>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          Booking details and consent are held on the clinic&rsquo;s own Phorest system, operated and
          secured by the clinic, which issues its own privacy notice to you at the time of booking.
          Session notes are taken and stored in the same way described above (Notability → OneDrive).
        </p>

        <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3 mt-6">Research data</h3>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          CORP research data is accessed only via password-protected systems.
        </p>

        {/* 9. Use of Artificial Intelligence (AI) Tools */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          9. Use of Artificial Intelligence (AI) Tools
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          Sharon Clare Hypnotherapy may use AI tools for general business administration — such as
          drafting correspondence, scheduling support, marketing content, and document preparation.
          The following safeguards apply:
        </p>
        <ul className="list-disc list-inside text-gray-700 leading-relaxed text-base mb-4 space-y-2">
          <li>
            No identifiable client health information, session notes, or therapy content is entered
            into general-purpose AI tools. Where AI assistance touches anything client-related,
            information is anonymised or pseudonymised first.
          </li>
          <li>
            AI tools are not given direct access to client record systems or folders (OneDrive client
            records, Notability, or the clinics&rsquo; Jane and Phorest systems).
          </li>
          <li>
            AI is never used to make decisions about your therapy, diagnosis, or treatment. All
            clinical judgement remains solely with Sharon Clare.
          </li>
          <li>
            Any AI provider used is expected to meet the same confidentiality and data-security
            standards as any other supplier, and is not used to store or train on your personal data.
          </li>
        </ul>

        {/* 10. Confidentiality */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          10. Confidentiality
        </h2>
        <ul className="list-disc list-inside text-gray-700 leading-relaxed text-base mb-4 space-y-2">
          <li>All sessions are strictly confidential.</li>
          <li>
            Your therapist may share limited, non-identifying details with a clinical supervisor for
            best-practice purposes.
          </li>
          <li>
            Information is only disclosed without consent if there is serious risk of harm to you or
            others, or if required by law, warrant, or court order.
          </li>
          <li>
            If you meet Sharon Clare outside a session, she will protect your privacy and not
            initiate conversation. You are free to discuss your therapy with others present if you
            choose.
          </li>
        </ul>

        {/* 11. Online Sessions */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          11. Online Sessions
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          Sharon Clare Hypnotherapy provides online sessions using Zoom.
        </p>

        <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3 mt-6">
          Your responsibilities
        </h3>
        <ul className="list-disc list-inside text-gray-700 leading-relaxed text-base mb-4 space-y-2">
          <li>Provide consent for online therapy.</li>
          <li>If you are under 18, written consent must come from a responsible adult.</li>
          <li>Ensure a quiet, private space for the session with a reliable internet connection.</li>
          <li>You&rsquo;ll need a camera, microphone, and speakers.</li>
          <li>
            Have a comfortable seat or sofa and a pillow to support your head during trance work.
          </li>
          <li>Remain visible on camera at all times, especially during hypnosis.</li>
          <li>
            For children, sessions cannot take place in a bedroom, and a parent must be present
            throughout.
          </li>
        </ul>

        <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3 mt-6">
          Therapist responsibilities
        </h3>
        <ul className="list-disc list-inside text-gray-700 leading-relaxed text-base mb-4 space-y-2">
          <li>Assess the suitability of online therapy for your needs.</li>
          <li>Take reasonable steps to ensure your environment is appropriate.</li>
          <li>
            Advise you to identify a local support person (friend, family member, or health
            practitioner) in case of emergency.
          </li>
        </ul>

        {/* 12. Technology and Privacy */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          12. Technology and Privacy
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          The systems used for scheduling, notes and records (including Calendly, Notability,
          OneDrive, and the clinics&rsquo; own Jane and Phorest systems) apply industry-standard
          encryption and access controls. The privacy of any internet communication is, however,
          limited by the security of the technology and network used at each end.
        </p>

        {/* 13. Contact Form Data */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          13. Contact Form Data
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          When you submit a contact form on this website, we collect your name, email address, and
          any information you include in your message. We use this information solely to respond to
          your enquiry. We do not share this data with third parties, and we retain it only for as
          long as necessary to handle your enquiry.
        </p>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          The legal basis for processing contact form data is our legitimate interests in responding
          to enquiries and providing our services (Article 6(1)(f) UK GDPR).
        </p>

        {/* 14. Quiz Data and Health Information */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          14. Quiz Data and Health Information
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          If you complete one of our online quizzes (Stress Symptoms, Medical Anxiety, or Bladder
          Urgency), we collect your name, email address, quiz responses, and your result tier. Your
          quiz responses may include information about your health and wellbeing, which is classified
          as special-category data under UK GDPR.
        </p>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          We ask for your explicit consent before storing this data. You will be asked to check a
          separate consent checkbox confirming that you consent to us storing your quiz responses,
          which may include health-related information, for up to 12 months. This is a requirement
          of Article 9(2)(a) UK GDPR for processing special-category health data.
        </p>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          <strong>Retention:</strong> If you do not go on to book a session or remain subscribed to
          our emails, we will delete your quiz response data after 12 months. If you become a
          client, your data may be retained for longer in accordance with the clinical
          record-keeping obligations set out in section 5.
        </p>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          Your quiz data is stored securely in Supabase, a cloud database provider, within the
          European Economic Area.
        </p>

        {/* 15. Email Marketing — Kit/ConvertKit */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          15. Email Marketing and Kit (ConvertKit)
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          If you opt in to receive email updates — either through a contact form, quiz, or
          subscription — we will add your email address and first name to our email marketing
          platform, <strong>Kit (formerly ConvertKit)</strong>.
        </p>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          <strong>Kit is a data processor acting on our behalf.</strong> This means Kit stores and
          processes your email address and name in order to send emails on our instruction. Kit&rsquo;s
          privacy policy can be found at{' '}
          <a
            href="https://kit.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-600 hover:text-accent-700 underline"
          >
            kit.com/privacy
          </a>
          . Kit is based in the United States and participates in the EU-US Data Privacy Framework.
        </p>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          The legal basis for adding your details to Kit is your explicit consent (Article 6(1)(a)
          UK GDPR). You can withdraw consent and unsubscribe at any time by clicking the unsubscribe
          link in any email we send you.
        </p>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          When you complete a quiz, we apply a tag to your subscriber record in Kit that records
          which quiz you completed and your result tier (for example,{' '}
          <code className="bg-gray-100 px-1 rounded text-sm">quiz-stress-symptoms-tier-2</code>).
          This allows us to send you relevant follow-up information.
        </p>

        {/* 16. Cookies and Analytics */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          16. Cookies and Analytics
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          Analytics and advertising tools are only used if you accept them in the cookie banner. With
          your consent, this website uses Google Analytics and Google Ads (to understand visits and
          measure which adverts lead to bookings; when you book, a hashed version of your email may
          be shared with Google Ads to measure conversions), the Meta Pixel (to measure Facebook and
          Instagram adverts) and Microsoft Clarity (heatmaps and anonymised session recordings of
          public pages).
        </p>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          You can change or withdraw your choice at any time using &ldquo;Cookie settings&rdquo; in the
          website footer. Full details of each cookie are in our{' '}
          <a href="/cookie-policy" className="underline">Cookie Policy</a>.
        </p>

        {/* 17. Your Rights */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          17. Your Rights
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          Under UK GDPR, you have the following rights regarding your personal data:
        </p>
        <ul className="list-disc list-inside text-gray-700 leading-relaxed text-base mb-4 space-y-2">
          <li><strong>Right to access</strong> — you can request a copy of the data we hold about you</li>
          <li><strong>Right to rectification</strong> — you can ask us to correct inaccurate data</li>
          <li><strong>Right to erasure</strong> — you can ask us to delete your personal data, subject to the retention obligations in section 5</li>
          <li><strong>Right to restrict processing</strong> — you can ask us to limit how we use your data</li>
          <li><strong>Right to withdraw consent</strong> — you can withdraw consent for email marketing or quiz data storage at any time</li>
          <li>
            <strong>Right to complain</strong> — you have the right to lodge a complaint with the
            Information Commissioner&rsquo;s Office (ICO) at{' '}
            <a
              href="https://ico.org.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-600 hover:text-accent-700 underline"
            >
              ico.org.uk
            </a>
          </li>
        </ul>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          To exercise any of these rights, please get in touch at{' '}
          <a
            href="mailto:sharon@sharonclare-hypnotherapy.com"
            className="text-accent-600 hover:text-accent-700 underline"
          >
            sharon@sharonclare-hypnotherapy.com
          </a>
          . We will respond within 30 days.
        </p>

        {/* 18. Changes */}
        <h2 className="text-[28px] font-heading font-bold text-gray-900 mb-4 mt-10">
          18. Changes to This Policy
        </h2>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          This document is a general statement and is not a substitute for independent legal advice.
          Given the number of systems and locations involved, Sharon Clare Hypnotherapy periodically
          reviews this statement and its data-sharing arrangements with clinic partners. The date at
          the top of this page shows when it was last updated.
        </p>
        <p className="text-gray-700 leading-relaxed text-base mb-4">
          See also our{' '}
          <a href="/terms" className="text-accent-600 hover:text-accent-700 underline">
            payment, cancellation and online terms
          </a>
          .
        </p>
      </div>
    </>
  );
};

export default PrivacyPolicy;
